import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Rate card master + proposal pricing.
 *
 * Every price on the proposal screen comes from the active rate card — the
 * backend does the arithmetic, the UI only collects inputs and renders what
 * comes back. Nothing is computed here.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) return new Error(data?.message || "Only a superadmin can edit the rate card.");
  if (status === 404) return new Error(data?.message || "Rate card not found.");
  // 422 carries the real reason ("Minimum 10 marks") — always prefer it.
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

/**
 * What a mutation hands back.
 *
 * The API writes its own sentence for what just happened ("Email sent to client
 * successfully."), and that is the one worth showing — an invented toast can
 * only ever be a worse guess at what the server actually did.
 */
const result = (body) => ({ data: body?.data ?? null, message: body?.message || "" });

export const fetchActiveRateCard = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/rate-cards/active`, { headers: jsonHeaders() });
    const body = res.data || {};
    if (!body.data) throw new Error(body.message || "No active rate card");
    return body.data;                       // { id, name, config }
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "Failed to load the rate card");
  }
};

/** The whole config replaces the stored one — partial keys are not accepted. */
export const updateRateCard = async (id, { name, config }) => {
  try {
    const res = await axios.put(
      `${API_BASE_URL}/rate-cards/${id}`,
      { ...(name ? { name } : {}), config },
      { headers: jsonHeaders() },
    );
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The rate card could not be saved");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The rate card could not be saved");
  }
};

/**
 * The editable config: company, cover subtitles, footer, validity, and both
 * documents' section lists. The generator does not use this — it asks
 * /proposal-template/rendered, which resolves the segment and the order.
 */
export const fetchProposalTemplate = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposal-template`, {
      headers: jsonHeaders(),
    });
    return res.data?.data || null;
  } catch (err) {
    throw apiError(err, "Failed to load the proposal template");
  }
};

/**
 * Save one block of the proposal template.
 *
 * Section-wise on purpose: the API deep-merges the patch, so two people editing
 * different sections cannot clobber each other the way a full-config PUT would.
 * Objects merge; arrays are replaced whole (a list's new order IS the value).
 *
 * POST, not PATCH: the real PATCH route died on CORS preflight behind the proxy.
 * Same body, a method every proxy passes through.
 */
export const patchProposalTemplate = async (patch) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/proposal-template/update`, { patch }, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The content could not be saved");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The content could not be saved");
  }
};

/**
 * Upload an image used by the documents (currently the founder photo).
 *
 * Returns an absolute URL, which is what the document needs: it is printed to
 * PDF and emailed, where a relative path resolves to nothing.
 */
export const uploadProposalImage = async (file) => {
  try {
    const fd = new FormData();
    fd.append("image", file);
    const res = await axios.post(`${API_BASE_URL}/proposal-template/upload-image`, fd, {
      // Let the browser set the multipart boundary; only auth is ours to add.
      headers: { ...authHeaders() },
    });
    const body = res.data || {};
    const url = body.data?.url || body.url;
    if (!url) throw new Error(body.message || "The upload did not return a URL");
    return url;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The image could not be uploaded");
  }
};

/**
 * A document exactly as it should print: sections already ordered, already
 * filtered to the enabled ones, already resolved for this segment.
 *
 * @param {"proposal"|"quotation"} doc
 * @param {string} segment corporate | attorney | a custom segment slug
 */
export const fetchRenderedTemplate = async (doc, segment) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposal-template/rendered`, {
      headers: jsonHeaders(),
      params: { doc, ...(segment ? { segment } : {}) },
    });
    return res.data?.data || res.data || null;
  } catch (err) {
    throw apiError(err, "Failed to load the document");
  }
};

/* The four section endpoints below all take `doc`, because the proposal and the
   quotation are separate documents with separate section lists. */

/** @param {string[]} order section keys, in the order they should print */
export const reorderSections = async (doc, order) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposal-template/sections/reorder`,
      { doc, order },
      { headers: jsonHeaders() },
    );
    return result(res.data);
  } catch (err) {
    throw apiError(err, "The order could not be saved");
  }
};

/**
 * @param {{title?: string, enabled?: boolean, content?: object}} patch
 *        `content` replaces the section's content wholesale — it is not merged,
 *        so send the complete object.
 */
export const updateSection = async (doc, key, patch) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposal-template/sections/update`,
      { doc, key, patch },
      { headers: jsonHeaders() },
    );
    return result(res.data);
  } catch (err) {
    throw apiError(err, "The section could not be saved");
  }
};

export const addSection = async (doc, section, position) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposal-template/sections`,
      { doc, section, ...(position === undefined ? {} : { position }) },
      { headers: jsonHeaders() },
    );
    return result(res.data);
  } catch (err) {
    throw apiError(err, "The section could not be added");
  }
};

export const deleteSection = async (doc, key) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposal-template/sections/delete`,
      { doc, key },
      { headers: jsonHeaders() },
    );
    return result(res.data);
  } catch (err) {
    throw apiError(err, "The section could not be deleted");
  }
};

/** Subscription plans — the options behind each tier's "map a plan" dropdown. */
/**
 * @param {{includeProposalOnly?: boolean}} [opts]
 *        Enterprise is split into hidden band-plans that exist only to be mapped
 *        to a tier. They belong in the Rate Card's plan picker and nowhere a
 *        customer looks, so the flag is opt-in rather than the default.
 */
export const fetchAllPlans = async ({ includeProposalOnly = false } = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-all-plans-v1`, {
      headers: jsonHeaders(),
      params: includeProposalOnly ? { include_proposal_only: 1 } : {},
    });
    const list = res.data?.data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    throw apiError(err, "Failed to load plans");
  }
};

/**
 * Package dropdown for a segment; the API already includes the "auto" option.
 *
 * The whole body is returned, not just the list, because the response also
 * carries settings that belong to no single package —
 * `default_payment_link_expiry_hours` among them. The hooks pick out the part
 * each caller wants, so this stays one request however many of them read it.
 */
export const fetchProposalPackages = async (segment) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/packages`, {
      headers: jsonHeaders(),
      params: { segment },
    });
    return res.data || {};
  } catch (err) {
    throw apiError(err, "Failed to load packages");
  }
};

/**
 * Live pricing for the proposal form.
 * Returns the `data` object, which is either a full quote or, past the
 * enterprise threshold, `{ ok, custom: true, plan, message }`.
 */
export const calculateProposal = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/proposals/calculate`, payload, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422) throw new Error(body.message || "Check the inputs");
    /* `ok: false` is the API refusing to price these inputs — a volume under the
       floor, say. It carries the sentence to show, so it is raised, not
       rendered as an empty quote. */
    const data = body.data;
    if (data && data.ok === false) throw new Error(data.message || "Check the inputs");
    return data;
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "Could not calculate this quote");
  }
};

/* ── Segments and tiers ────────────────────────────────────────────────────
   Pricing is rows, not a config blob: one `rate_cards` row per band, grouped by
   segment. A tier is either flat (`price_monthly`) or per-trademark
   (`per_mark_rate_yr` + `floor_monthly`) — never both.                       */

/** The tabs on the Rate Card screen: corporate, attorney, and anything added. */
export const fetchSegments = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/proposals/segments`, {
      headers: jsonHeaders(),
    });
    const list = res.data?.data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    throw apiError(err, "Failed to load the segments");
  }
};

/** `is_attorney` is assigned by the server (MAX + 1), so it is never sent. */
export const createSegment = async ({ slug, label }) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/proposals/segments`, { slug, label }, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The segment could not be created");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The segment could not be created");
  }
};

export const fetchTiers = async (segment) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/rate-cards/tiers`, {
      headers: jsonHeaders(),
      params: { segment },
    });
    const list = res.data?.data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    throw apiError(err, "Failed to load the tiers");
  }
};

export const createTier = async (tier) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/rate-cards/tiers`, tier, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The tier could not be saved");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The tier could not be saved");
  }
};

export const updateTier = async (id, tier) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/rate-cards/tiers/${id}`, tier, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The tier could not be saved");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The tier could not be saved");
  }
};

export const deleteTier = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE_URL}/rate-cards/tiers/${id}`, {
      headers: jsonHeaders(),
    });
    return result(res.data);
  } catch (err) {
    throw apiError(err, "The tier could not be deleted");
  }
};

/** The feature master list, for tiers that carry their own instead of a plan's. */
export const fetchFeatureMaster = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/get-features`, { headers: jsonHeaders() });
    const list = res.data?.data;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    throw apiError(err, "Failed to load the features");
  }
};

/**
 * What a tier will actually give its customers.
 *
 * The API resolves this: a tier with its own `features` gets those, one without
 * gets its linked plan's. Sending both ids on an edit is what lets it decide —
 * asking by plan alone would hide an override, and by tier alone would leave a
 * brand-new tier with nothing to seed from.
 *
 * @param {{planId?: number, rateCardId?: number, segment?: string}} opts
 * @returns {Promise<{features: Array<{id, name, highlight}>, featureIds: number[]}>}
 */
export const fetchPlanFeatures = async ({ planId, rateCardId, segment } = {}) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/rate-cards/plan-features/${planId || 0}`, {
      headers: jsonHeaders(),
      params: {
        ...(rateCardId ? { rate_card_id: rateCardId } : {}),
        ...(segment ? { segment } : {}),
      },
    });
    const data = res.data?.data || {};
    return {
      features: Array.isArray(data.features) ? data.features : [],
      // Already the resolved set — no matching names back to ids here.
      featureIds: Array.isArray(data.feature_ids) ? data.feature_ids.map(Number) : [],
    };
  } catch (err) {
    throw apiError(err, "Failed to load the plan's features");
  }
};

/**
 * How many trademarks a set of attorney numbers holds.
 *
 * A firm quotes on the marks it actually has, and nobody counts those by hand.
 * The reply is per number as well as a total, so a number that returns nothing
 * can be shown as unresolved instead of silently counting as zero.
 *
 * @param {string[]} attorneyNumbers
 * @returns {Promise<{total: number, details: Array<{attorney_number: string, trademarks: number, found: boolean}>}>}
 */
export const fetchAttorneyTrademarks = async (attorneyNumbers) => {
  try {
    const res = await axios.post(
      `${API_BASE_URL}/proposals/attorney-trademarks`,
      { attorney_numbers: attorneyNumbers },
      { headers: jsonHeaders() },
    );
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "Those attorney numbers could not be looked up");
    }
    const data = body.data || {};
    return {
      total: Number(data.total) || 0,
      details: Array.isArray(data.details) ? data.details : [],
    };
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "Those attorney numbers could not be looked up");
  }
};

/**
 * Edit a segment — including its per-mark taper, which is where an attorney
 * price actually lives. Changing a rate here changes every quote from the next
 * keystroke on, so it is a segment-level edit, not a per-tier one.
 *
 * @param {number} id
 * @param {{taper?: Array<{up_to: number|null, rate: number}>, label?: string,
 *          is_active?: boolean, sort_order?: number}} patch
 */
export const updateSegment = async (id, patch) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/proposals/segments/${id}`, patch, {
      headers: jsonHeaders(),
    });
    const body = res.data || {};
    if (body.status === 422 || body.status === false) {
      throw new Error(body.message || "The segment could not be saved");
    }
    return result(body);
  } catch (err) {
    if (err instanceof Error && !err.response) throw err;
    throw apiError(err, "The segment could not be saved");
  }
};
